import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPI, useGetUser } from 'hooks';
import Locales from 'locales';
import { getNormalizedOrganizationIds, getNormalizedOrganizationNames, getUserMembershipWriteFields } from 'helpers';
import { OrganizationContext, OrganizationField, UserIdentityFields, UserModal } from './UserFormPrimitives';
import { mergeMembershipOptions } from '../userManagement';

const EditUserSchema = z.object({
  firstName: z.string().trim().min(1, Locales.user.redesign.validation.first_names_required),
  lastName: z.string().trim().min(1, Locales.user.redesign.validation.last_name_required),
  organizationIds: z.array(z.string()).min(1, Locales.user.errors.organization_required),
});

const EditUserModal = ({ isOpen, onClose, user, organizations = [] }) => {
  const copy = Locales.user.redesign;
  const { user: currentUser, isSuperAdmin, refreshCurrentUser, activeOrganizationName } = useGetUser();
  const queryClient = useQueryClient();
  const { api } = useAPI();

  const currentOrganizationIds = useMemo(() => getNormalizedOrganizationIds(user), [user]);
  const currentOrganizationNames = useMemo(() => getNormalizedOrganizationNames(user), [user]);
  const form = useForm({
    resolver: zodResolver(EditUserSchema),
    defaultValues: {
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      organizationIds: currentOrganizationIds,
    },
  });

  useEffect(() => {
    form.reset({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      organizationIds: currentOrganizationIds,
    });
  }, [currentOrganizationIds, form, user]);

  const organizationOptions = useMemo(
    () => mergeMembershipOptions([...organizations].reverse(), currentOrganizationIds, currentOrganizationNames),
    [currentOrganizationIds, currentOrganizationNames, organizations]
  );

  const { mutate: editUser, isLoading } = useMutation({
    mutationFn: (data) =>
      api.update(
        'user',
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: user.email,
          // Super administrators may change memberships. Everyone else
          // resubmits the current list so the backend keeps existing roles.
          ...getUserMembershipWriteFields(user, isSuperAdmin ? data.organizationIds : currentOrganizationIds),
          is_onboard: true,
        },
        user.id
      ),
    onSuccess: async ({ data }) => {
      if (currentUser?.id === data.id) {
        await refreshCurrentUser();
      }

      queryClient.invalidateQueries({ queryKey: ['user/'] });
      toast.success(
        Locales.formatString(Locales.user.toast.user_updated, {
          firstName: data.first_name,
          lastName: data.last_name,
        })
      );
      onClose();
    },
  });

  return (
    <UserModal
      isOpen={isOpen}
      onClose={onClose}
      title={copy.edit_title}
      description={copy.edit_description}
      primaryLabel={copy.edit_primary_action}
      form={form}
      formId="edit-user-form"
      isSubmitting={isLoading}
      onSubmit={(data) => editUser(data)}
    >
      <UserIdentityFields control={form.control} emailReadOnly emailValue={user.email} />

      {isSuperAdmin ? (
        <OrganizationField control={form.control} organizations={organizationOptions} disabled={isLoading} />
      ) : (
        <OrganizationContext organizationName={activeOrganizationName} description={copy.edit_organization_context} />
      )}
    </UserModal>
  );
};

EditUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    organization_ids: PropTypes.arrayOf(PropTypes.string),
    organization_names: PropTypes.arrayOf(PropTypes.string),
    membership_version: PropTypes.number,
  }).isRequired,
};

export default EditUserModal;
